import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema } from './primitives.js';
import { LeadActivitySchema } from './leads.js';
const c = initContract();
/**
 * Opportunities / Pipeline (PART 10, CRM-02) and the tenant's ONE configured
 * stage list (owner ruling R1 — BR-CRM-02-21: "There is exactly one stage
 * pipeline: the tenant's configured list. Every dropdown, board column,
 * filter, panel and calculation must read that one list.").
 *
 * Permission area: `crm` (PART 16.2.2 row 5), per action:
 *   read = crm.view · create = crm.create · stage moves, probability,
 *   close won/lost, log activity = crm.edit · CONFIGURE STAGES = crm.approve
 *   — CRM-02 §9's "pipeline-configuration permission" resolves to the crm
 *   area's approve action: it is the one crm rung the Accountant's RCU row
 *   lacks and the Administrator holds, exactly the shape PART 16.4.2 draws.
 *
 * CLOSE WON (BR-CRM-02-12, BR-CRM-02-13, BR-CRM-02-14) — what the spec says it creates, verbatim:
 * "Closing a deal won must set the stage to Won, the probability to 100,
 * clear any manual override, and stamp the deal with the chosen document type
 * and its generated number"; "The generated document must be pre-filled with
 * the deal's customer, its items of interest and its value." Here that is a
 * DRAFT sales order (or quotation) for the deal's partner: one line per item
 * of interest with the deal's value allocated across them (a single line
 * named after the deal when there are no items), no tax and no discount, so
 * the draft's grand total equals the deal's value to the cent.
 *
 * Won/Lost are NOT stage rows (Q-CRM-02-4 kept): they are the fixed `status`
 * values, recognised by identifier while every other stage is data.
 *
 * Stalled (BR-CRM-02-17, BR-CRM-02-18): computed at read time for every reader — open
 * AND (expected close passed OR > 14 idle days since the last activity, or
 * creation when the log is empty). Never stored.
 */
// ── Vocabularies ────────────────────────────────────────────────────────────
/** Field 17: the five loss reasons. */
export const OPP_LOSS_REASONS = ['price', 'competitor', 'no_budget', 'timing', 'other'];
export const OPP_LOSS_REASON_LABELS = {
    price: 'Price',
    competitor: 'Competitor',
    no_budget: 'No Budget',
    timing: 'Timing',
    other: 'Other',
};
/**
 * CRM-02 §5c: what a NEW tenant's configured list is seeded with — "a factory
 * seed, and nothing more". Seeded at signup; never substituted for the
 * configured list on any code path (BR-CRM-02-21).
 */
export const FACTORY_OPP_STAGES = [
    { name: 'Qualification', probability: 20, color: 'blue' },
    { name: 'Needs Analysis', probability: 40, color: 'amber' },
    { name: 'Proposal Sent', probability: 60, color: 'violet' },
    { name: 'Negotiation', probability: 80, color: 'teal' },
];
/** BR-CRM-02-17: idle days above this make an open deal stalled. */
/**
 * EC-19-62 / F-109 — the stalled window. "Correct as it stands. The 14 days
 * SHOULD become a company setting" — still a constant today; flagged in the
 * rule-queue report as the remainder.
 */
export const STALLED_IDLE_DAYS = 14;
// ── The fixed sentences of CRM-02 ───────────────────────────────────────────
export const OPP_MESSAGES = {
    /**
     * BR-CRM-02-01 — the create refusal when called some other way, and the
     * server floor under ERR-CRM-02-01 (a name only) and ERR-CRM-02-02 (a value
     * of zero); "above zero" is the half that makes the second one a refusal.
     */
    incomplete: 'A deal needs a name, a customer and a value above zero.',
    /**
     * BR-CRM-02-25, word for word — and it is ALSO the sentence a switched-off
     * stage gets, rather than a second one invented for it.
     *
     * BR-DATA-06 says a switched-off record "must stop being offered on new
     * ones", and from the caller's side an inactive stage and a stage that was
     * never in the list are the same event: something they could pick a moment
     * ago is no longer pickable. BR-CRM-02-25's own advice — "Pick a current
     * stage" — is exactly right for it, and MD-11 §14 sets the precedent that
     * the spec's sentence is preferred to a new one wherever it reads true.
     */
    stageNotInPipeline: 'That stage is no longer in your pipeline. Pick a current stage.',
    /**
     * BR-DATA-05's floor for the pipeline: the switch-off must leave the tenant
     * somewhere to go, and a pipeline with no active stage is a pipeline that
     * can accept no new deal at all — `create` has nothing to offer. Named, and
     * with the way out, per BR-DATA-04's shape.
     */
    lastActiveStage: (name) => `${name} is the only stage still switched on. Switch another one on first.`,
    /** CRM-02 §7: won and lost are terminal; there is no path back. */
    closed: 'This deal is closed. A won or lost deal can no longer change.',
    /** BR-CRM-02-10 / ERR-CRM-02-05: Advance is not offered on the last configurable stage. */
    lastStage: 'This deal is on the last stage. Close it won or lost.',
    /** BR-CRM-02-15 / ERR-CRM-02-03 — the close-lost gate. */
    lossReasonRequired: 'Choose a reason before closing this deal lost.',
    /** §6: the composer refuses an empty entry. */
    emptyActivity: 'Type something before adding it to the log.',
};
/**
 * D46 — CRM-02's last-active-stage refusal, structured. `name` is the stage's
 * display name. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const OPP_REFUSALS = {
    lastActiveStage: (name) => ({
        message: OPP_MESSAGES.lastActiveStage(name),
        key: 'crm.errLastActiveStage',
        params: { name },
    }),
};
/** The stored log sentences (§6 drag rules, §13 walkthrough). */
export const OPP_LOG = {
    created: 'Opportunity created.',
    /** BR-CRM-01-13's "one extra entry recording the conversion". */
    convertedFrom: (leadCode) => `Converted from lead ${leadCode}.`,
    stageAuto: (stageName, pct) => `Stage → ${stageName}. Probability auto-set to ${pct}%.`,
    stageKept: (stageName, pct) => `Stage → ${stageName}. Probability kept at ${pct}% (manual override).`,
    probabilitySet: (pct) => `Probability set to ${pct}% (manual override).`,
    probabilityReset: (pct) => `Probability reset to the stage default (${pct}%).`,
    closedWon: (documentLabel, code) => `Closed Won → ${documentLabel} ${code} generated (customer, items and value pre-filled).`,
    closedLost: (reasonLabel) => `Closed Lost — ${reasonLabel}`,
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const OppLossReasonSchema = z.enum(OPP_LOSS_REASONS);
export const OppStatusSchema = z.enum(['open', 'won', 'lost']);
export const OppStageSchema = z
    .object({
    id: z.uuid(),
    /** Advance walks the list by this (BR-CRM-02-10). 1-based, dense. */
    position: z.number().int().positive(),
    name: z.string(),
    /** The default win probability new arrivals take (§5c). */
    probability: z.number().int().min(0).max(100),
    color: z.string(),
    /**
     * BR-DATA-05, BR-DATA-06's switch-off. A false row keeps drawing the deals already
     * stored against it — `openDealCount` still counts them — and is offered
     * nowhere: not at create, not as a `setStage` target, and Advance steps
     * over it.
     */
    isActive: z.boolean(),
    /** Q-CRM-02-9: shown by the config screen — open deals sitting in the stage. */
    openDealCount: z.number().int(),
})
    .strict();
export const OppActivitySchema = LeadActivitySchema;
export const OppItemSchema = z
    .object({
    itemId: z.uuid(),
    code: z.string(),
    name: z.string(),
})
    .strict();
export const OpportunitySchema = z
    .object({
    id: z.uuid(),
    /** OPP-#### (field 2). */
    code: z.string(),
    oppNo: z.number().int().positive(),
    name: z.string(),
    /** Q-CRM-02-5: lookup with free-text fallback; set no later than Close Won. */
    partnerId: z.uuid().nullable(),
    customerName: z.string(),
    value: MoneyStringSchema,
    /** value × probability ÷ 100 (BR-CRM-02-05), 2 decimals. */
    weightedValue: z.string(),
    status: OppStatusSchema,
    stageId: z.uuid(),
    stageName: z.string(),
    stageColor: z.string(),
    probability: z.number().int().min(0).max(100),
    manualOverride: z.boolean(),
    expectedClose: IsoDateSchema.nullable(),
    salespersonId: z.uuid().nullable(),
    salespersonName: z.string().nullable(),
    /** BR-CRM-02-23: shown permanently. */
    fromLeadId: z.uuid().nullable(),
    fromLeadCode: z.string().nullable(),
    wonDocumentType: z.enum(['sales_order', 'quotation']).nullable(),
    wonOrderId: z.uuid().nullable(),
    wonOrderCode: z.string().nullable(),
    wonQuotationId: z.uuid().nullable(),
    wonQuotationCode: z.string().nullable(),
    lossReason: OppLossReasonSchema.nullable(),
    /** Q-CRM-02-3: the real closing date. */
    closedOn: IsoDateSchema.nullable(),
    /** BR-CRM-02-17: computed fresh, shown to every reader. */
    stalled: z.boolean(),
    stalledReason: z.enum(['close_date_passed', 'idle']).nullable(),
    /** BR-CRM-02-18: from the last activity, or creation; never negative. */
    idleDays: z.number().int(),
    items: OppItemSchema.array(),
    createdAt: z.string(),
})
    .strict();
export const OpportunityDetailSchema = OpportunitySchema.extend({
    activities: OppActivitySchema.array(),
}).strict();
export const CreateOpportunityRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    /** Either a partner lookup or free text — at least one (Q-CRM-02-5). */
    partnerId: z.uuid().optional(),
    customerName: z.string().trim().min(1).max(160).optional(),
    /** BR-CRM-02-01: above zero (server-checked; zero arrives only via convert). */
    value: MoneyStringSchema,
    /** BR-CRM-02-04: only configurable stages exist here — won/lost are not stages. */
    stageId: z.uuid(),
    expectedClose: IsoDateSchema.optional(),
    salespersonId: z.uuid().optional(),
    itemIds: z.uuid().array().max(50).optional(),
})
    .strict();
export const UpdateOpportunityRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    partnerId: z.uuid().nullable().optional(),
    customerName: z.string().trim().min(1).max(160).optional(),
    value: MoneyStringSchema.optional(),
    expectedClose: IsoDateSchema.nullable().optional(),
    salespersonId: z.uuid().nullable().optional(),
    /** Present = replace the chip set. */
    itemIds: z.uuid().array().max(50).optional(),
})
    .strict();
export const SetOppStageRequestSchema = z.object({ stageId: z.uuid() }).strict();
/** Type a probability (marks the override) OR reset to the stage default. */
export const SetOppProbabilityRequestSchema = z
    .union([
    z.object({ probability: z.number().int().min(0).max(100) }).strict(),
    z.object({ reset: z.literal(true) }).strict(),
]);
export const CloseWonRequestSchema = z
    .object({ documentType: z.enum(['sales_order', 'quotation']) })
    .strict();
export const CloseLostRequestSchema = z
    .object({
    reason: OppLossReasonSchema,
    note: z.string().trim().max(2000).optional(),
})
    .strict();
export const LogOppActivityRequestSchema = z
    .object({
    kind: z.enum(['note', 'call', 'email', 'meeting']),
    body: z.string().trim().min(1).max(2000),
})
    .strict();
/** Configure stages (crm.approve): rename and/or re-weight one stage. */
export const UpdateOppStageRequestSchema = z
    .object({
    name: z.string().trim().max(60).optional(),
    probability: z.number().int().min(0).max(100).optional(),
})
    .strict();
/** Reorder: the COMPLETE pipeline in its new order (BR-CRM-02-10's ground). */
export const ReorderOppStagesRequestSchema = z
    .object({ stageIds: z.uuid().array().min(1) })
    .strict();
/**
 * ── CRM-02's FIGURES ABOUT EVERY DEAL, WHICH ARE NOT FIGURES ABOUT A PAGE ──
 *
 * This is the route CRM-01 established (`GET /api/crm/leads/stats`) and SAL-02
 * repeated, for the reason `PAGINATION.md` §3 step 10 gives: *"a screen may
 * derive something from the WHOLE list without saying so"*. CRM-02 derived
 * five such things in the browser, over every deal the list returned —
 *
 *   the weighted pipeline and the raw pipeline (BR-CRM-02-06, OPEN deals);
 *   the stalled count (BR-CRM-02-17, computed per row);
 *   the by-stage panel, which is those same two sums grouped;
 *   the win rate (BR-CRM-02-19, DECIDED deals only);
 *   the average sales cycle (Q-CRM-02-3, the REAL closed-on date).
 *
 * — and a page turns every one of them into a quiet lie. So they became two
 * aggregates over the same predicate the list filters on, and the list became
 * a page in the same commit.
 *
 * `weighted` is summed as it is PRINTED: BR-CRM-02-05 rounds value ×
 * probability ÷ 100 per deal, so the total is the sum of rounded rows and not
 * the rounding of a sum. The two differ by cents, and the cents are the ones a
 * sales manager reconciles against the board.
 */
export const OppStatsSchema = z
    .object({
    /** BR-CRM-02-06: the headline counts OPEN deals only. */
    open: z
        .object({
        count: z.number().int(),
        weighted: MoneyStringSchema,
        raw: MoneyStringSchema,
    })
        .strict(),
    /** BR-CRM-02-17's computed flag, counted over every OPEN deal. */
    stalled: z.number().int(),
    /**
     * One row per configured stage, OPEN deals only — the §5 panel and the
     * board's column headers. A stage with no deals is present with zeros,
     * because the panel draws every configured stage (BR-CRM-02-21).
     */
    byStage: z
        .object({
        stageId: z.uuid(),
        count: z.number().int(),
        weighted: MoneyStringSchema,
        raw: MoneyStringSchema,
    })
        .strict()
        .array(),
    /** BR-CRM-02-19: the win rate's two denominators, never open deals. */
    won: z.number().int(),
    lost: z.number().int(),
    /**
     * Q-CRM-02-3's average sales cycle in whole days — won deals with a
     * positive span only. `null` when there are none, because a zero would
     * read as "we close same-day".
     */
    avgCycleDays: z.number().int().nullable(),
})
    .strict();
const oppParams = z.object({ opportunityId: z.uuid() }).strict();
const stageParams = z.object({ stageId: z.uuid() }).strict();
export const opportunitiesContract = c.router({
    listStages: {
        method: 'GET',
        path: '/api/crm/stages',
        query: z
            .object({
            /**
             * The pipeline-configuration panel, which has to see a switched-off
             * stage to switch it back on. Every other caller is a picker and
             * gets the active list (BR-DATA-06).
             */
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ stages: OppStageSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'THE configured pipeline, in order (crm.view) — the one list every dropdown, column and calculation reads (BR-CRM-02-21). Switched-off stages are left out unless includeInactive=true (BR-DATA-06).',
    },
    /**
     * BR-DATA-05's switch-off for §17.3.2's pipeline-stage row. Same
     * permission as every other pipeline change (BR-CRM-02-26): switching a
     * stage off changes what Advance means for every deal in the company,
     * exactly as reordering does.
     */
    deactivateStage: {
        method: 'POST',
        path: '/api/crm/stages/:stageId/deactivate',
        pathParams: stageParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ stages: OppStageSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch a stage off (crm.approve). Deals already in it are untouched and keep drawing under it; it stops being offered, and Advance steps over it. The last active stage is refused.',
    },
    reactivateStage: {
        method: 'POST',
        path: '/api/crm/stages/:stageId/reactivate',
        pathParams: stageParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ stages: OppStageSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch a stage back on (crm.approve) — its position and probability are untouched.',
    },
    updateStage: {
        method: 'PATCH',
        path: '/api/crm/stages/:stageId',
        pathParams: stageParams,
        body: UpdateOppStageRequestSchema,
        responses: {
            200: z.object({ stages: OppStageSchema.array() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Rename / re-weight a stage (crm.approve — the pipeline-configuration permission, BR-CRM-02-26). A blank rename falls back to the factory label (BR-CRM-02-22); a probability change never touches existing deals (Q-CRM-02-9).',
    },
    reorderStages: {
        method: 'PUT',
        path: '/api/crm/stages/order',
        body: ReorderOppStagesRequestSchema,
        responses: {
            200: z.object({ stages: OppStageSchema.array() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Reorder the pipeline (crm.approve). Deals stay put — they store the stage identifier — but Advance means something different immediately (§15).',
    },
    list: {
        method: 'GET',
        path: '/api/crm/opportunities',
        query: pagedQuery({
            q: z.string().optional(),
            status: OppStatusSchema.optional(),
            stageId: z.uuid().optional(),
            salespersonId: z.uuid().optional(),
            /** The stalled view: BR-CRM-02-17's computed flag as a filter. */
            stalled: z.literal('true').optional(),
        }),
        responses: {
            200: pagedResponse('opportunities', OpportunitySchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Deals (crm.view), PAGED (NFR-42), with weighted value, stalled flag and provenance computed per row. Every figure ABOUT every deal is `/stats` — see there.',
    },
    stats: {
        method: 'GET',
        path: '/api/crm/opportunities/stats',
        responses: { 200: OppStatsSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'The pipeline headline, the by-stage panel and the forecast tiles (crm.view) — aggregates over EVERY deal, so a page of the list above cannot narrow them.',
    },
    get: {
        method: 'GET',
        path: '/api/crm/opportunities/:opportunityId',
        pathParams: oppParams,
        responses: {
            200: OpportunityDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One deal with its full activity log (crm.view).',
    },
    create: {
        method: 'POST',
        path: '/api/crm/opportunities',
        body: CreateOpportunityRequestSchema,
        responses: {
            201: OpportunityDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New deal (crm.create): name + customer + value above zero (BR-CRM-02-01); takes the chosen stage’s default probability, no override, dated today (BR-CRM-02-03); the stage and the probability come from the ONE list in the SAME read (BR-CRM-02-24).',
    },
    update: {
        method: 'PATCH',
        path: '/api/crm/opportunities/:opportunityId',
        pathParams: oppParams,
        body: UpdateOpportunityRequestSchema,
        responses: {
            200: OpportunityDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit an OPEN deal (crm.edit). A closed deal answers 409 (CRM-02 §7).',
    },
    advance: {
        method: 'POST',
        path: '/api/crm/opportunities/:opportunityId/advance',
        pathParams: oppParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: OpportunityDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'One position along the configured order (crm.edit, BR-CRM-02-10); refused on the last stage. Probability resets unless overridden, and the log note says which (BR-CRM-02-07).',
    },
    setStage: {
        method: 'POST',
        path: '/api/crm/opportunities/:opportunityId/stage',
        pathParams: oppParams,
        body: SetOppStageRequestSchema,
        responses: {
            200: OpportunityDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Move to any configured stage (crm.edit) — the board drop. An out-of-pipeline stage is refused at write time (BR-CRM-02-25).',
    },
    setProbability: {
        method: 'POST',
        path: '/api/crm/opportunities/:opportunityId/probability',
        pathParams: oppParams,
        body: SetOppProbabilityRequestSchema,
        responses: {
            200: OpportunityDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Type a probability (0–100, marks the manual override — BR-CRM-02-08) or reset to the stage default (clears it — BR-CRM-02-09). crm.edit.',
    },
    closeWon: {
        method: 'POST',
        path: '/api/crm/opportunities/:opportunityId/close-won',
        pathParams: oppParams,
        body: CloseWonRequestSchema,
        responses: {
            201: z
                .object({
                opportunity: OpportunityDetailSchema,
                documentId: z.uuid(),
                documentCode: z.string(),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Close Won (crm.edit): stage → Won, probability 100, override cleared, and the chosen DRAFT document generated pre-filled with the customer, the items of interest and the value (BR-CRM-02-12, BR-CRM-02-13, BR-CRM-02-14). The deal leaves both pipeline totals.',
    },
    closeLost: {
        method: 'POST',
        path: '/api/crm/opportunities/:opportunityId/close-lost',
        pathParams: oppParams,
        body: CloseLostRequestSchema,
        responses: {
            200: OpportunityDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Close Lost (crm.edit): a reason from the five is mandatory (BR-CRM-02-15); probability 0, override cleared (BR-CRM-02-16).',
    },
    logActivity: {
        method: 'POST',
        path: '/api/crm/opportunities/:opportunityId/activities',
        pathParams: oppParams,
        body: LogOppActivityRequestSchema,
        responses: {
            201: OpportunityDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Add a note/call/email/meeting to the log (crm.edit). Resets the idle clock.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=opportunities.js.map